// Форматирование и разбор ответов каталога — чистые функции, без React.

import { HttpError } from '@/shared/api';
import { SupportReadOnlyError } from '@/shared/auth';
import { t } from '@/shared/i18n';

import type { Category, ShelfLifeUnit } from './types';

const UNIT_LABELS: Record<ShelfLifeUnit, readonly [string, string, string]> = {
  // [1, 2-4, 5-20] — русские формы множественного числа. Без них получается
  // «24 часы» и «2 дней», а срок годности — это то, что читают бегло.
  hours: ['час', 'часа', 'часов'],
  // СУТКИ, а не дни (решение владельца 2026-07-30). Так принято в пищевой отрасли,
  // и так уже записано в самом каталоге: категории названы «14 суток», «30 суток».
  // Приложение говорило «дней», и один и тот же срок назывался двумя словами.
  //
  // «Сутки» — существительное только множественного числа, поэтому форма для единицы
  // тоже «сутки»: «1 сутки», «2 суток», «5 суток». Не «сутка».
  days: ['сутки', 'суток', 'суток'],
  months: ['месяц', 'месяца', 'месяцев'],
};

function pluralForm(value: number): 0 | 1 | 2 {
  const mod100 = value % 100;
  if (mod100 >= 11 && mod100 <= 14) {
    return 2;
  }
  const mod10 = value % 10;
  if (mod10 === 1) {
    return 0;
  }
  if (mod10 >= 2 && mod10 <= 4) {
    return 1;
  }
  return 2;
}

/** «48 часов», «6 месяцев» — длительность категории человеческим языком. */
export function formatShelfLife(unit: ShelfLifeUnit, value: number): string {
  return `${value} ${UNIT_LABELS[unit][pluralForm(value)]}`;
}

/**
 * Мягкий предел длины названия продукта.
 *
 * Не запрет: у продукта бывает длинное официальное имя, и сервер принимает до 255.
 * Но рендер этикетки (`shared/printing/render/layout.ts`) переносит имя максимум
 * на две строки и дальше режет многоточием — а на узкой ленте 43×25 помещается
 * около 23 знаков. Подсказка предупреждает менеджера ДО того, как он увидит
 * обрезанное имя на бумаге.
 */
export const PRODUCT_NAME_SOFT_LIMIT = 40;
export const PRODUCT_NAME_HARD_LIMIT = 120;

/** Подсказка под полем имени, если оно длиннее мягкого предела. `undefined` — если норм. */
export function productNameHint(name: string): string | undefined {
  return name.trim().length > PRODUCT_NAME_SOFT_LIMIT
    ? t('catalog.product.nameTooLong')
    : undefined;
}

/** Подпись категории для селекта: имя и длительность рядом, чтобы не гадать. */
/**
 * Подпись категории в списке выбора.
 *
 * Название показывается ТОЛЬКО когда оно что-то добавляет. Стандартные категории
 * названы своей же длительностью (`name = '12 часов'` при `12 hours`), и прежняя
 * формула давала «12 часов — 12 часов»: тире читалось как диапазон, то есть «от 12
 * до 12». Владелец назвал это главной путаницей каталога, и он прав — строка сообщала
 * одно и то же дважды, да ещё в форме, намекающей на промежуток.
 *
 * Сравнение ТОЧНОЕ, с канонической формой длительности, а не «похоже на число часов»:
 * нечёткое совпадение однажды спрячет осмысленное название вроде «12 часов на льду».
 *
 * Разделитель — средняя точка, а не тире: «Мясо · 24 часа» читается как «что» и
 * «сколько», тогда как тире между ними по-прежнему намекало бы на диапазон.
 */
export function formatCategoryOption(category: Category): string {
  const shelfLife = formatShelfLife(category.unit, category.value);
  return isJustTheDuration(category) ? shelfLife : `${category.name.trim()} · ${shelfLife}`;
}

/**
 * Синонимы единиц: разные слова для одного и того же срока.
 *
 * Основное слово для дней — «сутки». «День» остаётся синонимом: категорию могли назвать
 * руками «7 дней», и без этого списка строка вышла бы «7 дней · 7 суток» — тот же
 * повтор, от которого мы и уходили, только другими словами.
 */
const UNIT_SYNONYMS: Partial<Record<ShelfLifeUnit, readonly [string, string, string]>> = {
  days: ['день', 'дня', 'дней'],
};

/** Имя категории не добавляет ничего к её длительности. */
function isJustTheDuration(category: Category): boolean {
  const name = category.name.trim();
  const forms = [UNIT_LABELS[category.unit], UNIT_SYNONYMS[category.unit]].filter(
    (item): item is readonly [string, string, string] => item !== undefined,
  );

  return forms.some(
    (labels) => name === `${String(category.value)} ${labels[pluralForm(category.value)]}`,
  );
}

/**
 * Сообщение об ошибке для пользователя.
 *
 * Backend отдаёт единый конверт `{error: {code, message, details}}` — берём из него
 * `message`, потому что он уже написан по-русски и по делу. Молча глотать ошибку
 * или показывать «Something went wrong» запрещено (code-quality.md §6): на кухне
 * человек должен понимать, почему действие не прошло.
 */
export function extractErrorMessage(error: unknown): string {
  if (error instanceof HttpError) {
    const payload = error.payload as { error?: { message?: string } } | undefined;
    if (payload?.error?.message) {
      return payload.error.message;
    }
    return t('catalog.error.http');
  }
  // Отказ саппорт-сессии — не обрыв связи, хоть и не HTTP: очередь отклоняет запись
  // до сервера. Без этой ветки саппорт видел «Нет связи с сервером» (QA ISSUE-002).
  if (error instanceof SupportReadOnlyError) {
    return error.message;
  }
  if (error instanceof Error) {
    return t('catalog.error.network');
  }
  return t('catalog.error.unknown');
}

/**
 * Ошибки по полям формы из того же конверта.
 *
 * DRF кладёт их в `details` как `{field: ["текст"]}`. Возвращаем плоскую карту:
 * форме нужен один текст под полем, а не массив.
 */
export function extractFieldErrors(error: unknown): Record<string, string> {
  if (!(error instanceof HttpError)) {
    return {};
  }
  const details = (error.payload as { error?: { details?: unknown } } | undefined)?.error?.details;
  if (!details || typeof details !== 'object' || Array.isArray(details)) {
    return {};
  }

  const result: Record<string, string> = {};
  for (const [field, messages] of Object.entries(details as Record<string, unknown>)) {
    if (Array.isArray(messages) && typeof messages[0] === 'string') {
      result[field] = messages[0];
    } else if (typeof messages === 'string') {
      result[field] = messages;
    }
  }
  return result;
}
